import {
  DatePipe,
  isPlatformBrowser,
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  PLATFORM_ID,
  signal,
  WritableSignal
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule
} from '@angular/forms';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { RendezVous } from '../../../../../core/model/rendez-vous.model';
import { Seance } from '../../../../../core/model/seance.model';
import { ConfirmCancelSeanceComponent } from '../../confirm-cancel-seance/confirm-cancel-seance.component';
import { emulateClick } from '../../core/a11y';
import { DrawerService } from '../../drawer/drawer.service';
import { NotificationService } from '../../notification/notification.service';
import { AutoCloseDirective } from '../../shared/directives/auto-close.directive';
import { HoverGradientDirective } from '../../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../../shared/directives/progress-click.directive';
import { Center } from '../../shared/model/center.model';
import {
  Event,
  PlanningComponent
} from '../../shared/planning/planning.component';
import { AccountService } from '../../shared/services/account.service';
import { AuthService } from '../../shared/services/auth.service';
import { CenterService } from '../../shared/services/center.service';
import { SelectionService } from '../../shared/services/selection.service';

@Component({
  selector: 'app-vitality-planning',
  templateUrl: 'vitality-planning.html',
  imports: [
    AutoCloseDirective,
    NgOptimizedImage,
    NgxTolgeeModule,
    NgClass,
    PlanningComponent,
    DatePipe,
    HoverGradientDirective,
    ProgressClickDirective,
    ReactiveFormsModule
  ]
})
export class VitalityPlanningComponent {
  cd = inject(ChangeDetectorRef);
  authService = inject(AuthService);
  accountService = inject(AccountService);
  selectionService = inject(SelectionService);
  centerService = inject(CenterService);
  notificationService = inject(NotificationService);
  drawerService = inject(DrawerService);
  mode = input.required<'plan' | 'cancel'>();
  planning: WritableSignal<Seance[]> = signal([]);
  rendezVous = signal<RendezVous[] | null>(null)
  selectedSeance = signal<Seance | null>(null);
  viewing = computed(() => {
    const from = new Date();
    const to = new Date()
    to.setDate(to.getDate() + 21);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  });
  loading = signal(false);
  selectedCenter = signal<Center | null>(null);
  centerChoices = computed(() => {
    const centers = this.centerService.centers();
    const site = this.selectionService.center();
    if (!site) {
      return centers;
    }
    if ([ 7, 8, 9 ].includes(site.erpId)) {
      return centers.filter(c => [ 7, 8, 9 ].includes(c.erpId))
    }
    return null;
  })
  toggleHours = new FormControl(6, { nonNullable: true });
  protected readonly emulateClick = emulateClick;

  constructor() {
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      effect(async () => {
        const center = this.selectionService.center();
        if (center) {
          this.selectedCenter.set(center);
        }
      });
      effect(async () => {
        const center = this.selectedCenter();
        if (!center) {
          return;
        }
        await this.updatePlanning(center);
      });
    }
  }

  /**
   * Select current center
   * @param center The selected center
   */
  async selectCenter(center: Center): Promise<void> {
    if (this.loading()) {
      return;
    }
    this.selectedCenter.set(center);
    await this.updatePlanning(center);
  }

  /**
   * Set selected session
   * @param event Click on planning event
   */
  async handleEventClicked(event: Event): Promise<void> {
    if (!this.authService.user()?.authCompleted) {
      return;
    }
    if (this.selectedSeance()) {
      return;
    }
    const seance = event as Seance;
    this.selectedSeance.update(s => {
      if (!s || s.erpId !== seance.erpId) {
        return seance
      }
      return null;
    });
  }

  /**
   * Try to participate a session
   */
  participate = async (): Promise<void> => {
    const seance = this.selectedSeance();
    if (!seance) {
      return;
    }
    try {
      await this.accountService.participateSeance(seance);
      this.notificationService.open({
        type: 'success',
        title: '',
        message: 'participateSuccess',
        close: 3000
      });
    } catch {
      this.notificationService.open({
        type: 'error',
        title: 'cannotParticipate',
        message: 'cannotParticipateDescription'
      });
      return;
    }
    await this.refreshPlanning();
  }

  /**
   * Try to participate a session
   */
  wait = async (): Promise<void> => {
    const seance = this.selectedSeance();
    if (!seance) {
      return;
    }
    try {
      await this.accountService.waitSeance(seance, this.toggleHours.value);
      this.notificationService.open({
        type: 'success',
        title: '',
        message: 'waitSuccess',
        close: 3000
      });
      return;
    } catch {
      this.notificationService.open({
        type: 'error',
        title: 'cannotWait',
        message: 'cannotWaitDescription'
      });
    }
    await this.refreshPlanning();
  }

  /**
   * Try to participate a session
   */
  cancelWait = async (): Promise<void> => {
    const seance = this.selectedSeance();
    if (!seance) {
      return;
    }
    const rdv = this.rendezVous()?.find(rdv => rdv.seanceErpId === seance.erpId);
    if (!rdv) {
      return;
    }
    this.drawerService.open({
      component: ConfirmCancelSeanceComponent,
      inputs: { rendezVous: rdv },
      style: 'transparent'
    });
  }

  /**
   * Try to cancel a session
   */
  cancel = async (): Promise<void> => {
    const seance = this.selectedSeance();
    if (!seance) {
      return;
    }
    const rdv = this.rendezVous()?.find(rdv => rdv.seanceErpId === seance.erpId);
    if (!rdv) {
      return;
    }
    this.drawerService.open({
      component: ConfirmCancelSeanceComponent,
      inputs: { rendezVous: rdv },
      style: 'transparent'
    });
  }

  /**
   * Refresh planning rendez vous
   */
  async refreshPlanning(): Promise<void> {
    const viewing = this.viewing();
    const rendezVous = await this.accountService.rendezVous(viewing.from, viewing.to);
    this.accountService.dataChanged.set(new Date())
    this.rendezVous.set(rendezVous);
    this.selectedSeance.set(null);
    let seances = this.planning();
    for (const rdv of rendezVous) {
      const seance = seances.find(s => s.erpId === rdv.seanceErpId);
      if (seance) {
        seance.participating = rdv.waiting ? 'waiting' : 'yes';
      }
    }
    if (this.mode() === 'cancel') {
      seances = seances.filter(s => s.participating !== 'no')
    }
    this.planning.set(seances);
    await this.accountService.refreshCredits();
  }

  /**
   * Load planning of a selected center
   * @param center The selected center
   */
  private async updatePlanning(center: Center): Promise<void> {
    this.loading.set(true);
    this.selectedCenter.set(center);
    try {
      const viewing = this.viewing();
      this.rendezVous.set(null);
      const rendezVous: RendezVous[] =
        this.authService.user()?.authCompleted ?
          await this.accountService.rendezVous(viewing.from, viewing.to) :
          [];
      this.rendezVous.set(rendezVous);
      if (rendezVous) {
        let seances = await this.accountService.seances(center.erpId, viewing.from, viewing.to);
        for (const rdv of rendezVous) {
          const seance = seances.find(s => s.erpId === rdv.seanceErpId);
          if (seance) {
            seance.participating = rdv.waiting ? 'waiting' : 'yes';
          }
        }
        if (this.mode() === 'cancel') {
          seances = seances.filter(s => s.participating !== 'no')
        }
        this.planning.set(seances);
      }
    } catch {
      this.planning.set([]);
    }
    this.loading.set(false);
    this.cd.detectChanges();
  }
}
